/**
* GA4 helper for GTM dataLayer.
*/

const isBrowser = typeof window !== "undefined";
const channel = process.env.REACT_APP_DEFAULT_CHANNEL;

const getDataLayer = () => {
  if (!isBrowser) return null;

  if (!window.dataLayer) {
    window.dataLayer = [];
  }
  return window.dataLayer;
};

/**
 * Maps a cart item to GA4 item format.
 * Handles cart API structure: short_description, brand.label, seller (string), price.BRL.offer
 */
const mapCartItemToGA4 = (product, index) => {
  const price = parseFloat(product.price?.BRL?.offer || product.price?.BRL?.price || 0);
  const originalPrice = parseFloat(product.price?.BRL?.price || 0);

  return {
    item_id: product.uuid || product.id || "",
    item_name: product.label || product.short_description || "",
    affiliation: product.seller || "",
    discount: originalPrice && price ? Math.max(0, originalPrice - price) : 0,
    index: index,
    item_brand: product.brand_name || product.brand?.label || "",
    item_category: product.category_name || "",
    price: price,
    quantity: product.quantity || 1,
  };
};

/**
 * Maps a PDP product to GA4 item format.
 * Handles PDP API structure: brand.name, categories[].name, price.BRL.offer
 */
const mapPdpItemToGA4 = (product) => {
  const price = parseFloat(product.price?.BRL?.offer || 0);
  const originalPrice = parseFloat(product.price?.BRL?.price || 0);

  return {
    item_id: product.uuid || product.id || "",
    item_name: product.label || "",
    affiliation: product.seller_name || "",
    discount: originalPrice && price ? Math.max(0, originalPrice - price) : 0,
    index: 0,
    item_brand: product.brand?.name || "",
    item_category: product.categories?.[0]?.name || product.category_name || "",
    price: price,
    quantity: 1,
  };
};

/**
 * Maps a product list item (search/category/brand) to GA4 item format.
 * Handles both product_price (search API) and price.BRL (home API) structures.
 */
const mapListItemToGA4 = (product, index) => ({
  item_id: product.uuid || product.id || "",
  item_name: product.label || "",
  affiliation: product.seller_name || product.seller?.name || "",
  discount: product.product_price
    ? Math.max(
        0,
        (product.product_price.price_suggested || 0) -
          (product.product_price.price || 0),
      )
    : product.price?.BRL
      ? Math.max(0, parseFloat(product.price.BRL.price || 0) - parseFloat(product.price.BRL.offer || 0))
      : 0,
  index: index,
  item_brand: product.brand_name || product.brand?.name || "",
  item_category: product.product_categories?.[0]?.category_name || product.category_name || "",
  price: product.product_price?.price || parseFloat(product.price?.BRL?.offer || 0) || 0,
  quantity: 1,
});

/**
 * Calculates total value from GA4 items array.
 */
const calcTotalValue = (items) =>
  items.reduce((acc, item) => acc + item.price * item.quantity, 0);

/**
 * Sends an event to the GTM dataLayer.
 * @param {string} eventName - Event name.
 * @param {object} eventParams - Extra event params.
 */
export const sendGA4Event = (eventName, eventParams = {}) => {
  if (!eventName) return;
  const dataLayer = getDataLayer();
  if (!dataLayer) return;

  dataLayer.push({
    event: eventName,
    ...eventParams,
  });

  if (process.env.NODE_ENV === "development") {
    console.log("GA4 Event:", eventName, eventParams);
  }
};

export const sendGAEvent = sendGA4Event;

export const BUTTON_LABELS = {
  'relevant' : 'mais-relevantes',
  'discount_desc' : 'maiores-descontos',
  'smaller' : 'menor-preco',
  'larger' : 'maior-preco',
  'label_asc' : 'ordem-alfabetica-a-z',
  'label_desc' : 'ordem-alfabetica-z-a',
};

/**
* Reusable function for sending button click events to GA4/GTM dataLayer.
* Specifically designed for marketplace button interactions.
* 
* @param {object} params - Button parameters
* @param {string} params.custom_title - Button name (ex: "abrir-carrinho")
* @param {string} params.content_type - Category (ex: "header")
* @param {string} params.Nome_Da_Tela - Screen name (ex: "marketplace-home")
* @param {string} params.id_da_tag - Tag identifier
* @param {...*} params.additionalParams - Any additional parameters
*/
export const sendButtonEvent = (params = {}) => {
  const dataLayer = getDataLayer();
  if (!dataLayer) return;

  const {
    custom_title,
    content_type,
    Nome_Da_Tela,
    id_da_tag,
    event_type,
    ...additionalParams
  } = params;

  if (!custom_title) {
    console.warn("sendButtonEvent: custom_title is required");
  }

  const buttonEvent = {
    event: event_type || "select_content",
    elemento: "botao",
    custom_title: custom_title,
    content_type: content_type,
    Nome_Da_Tela: Nome_Da_Tela,
    funcionalidades: "marketplace",
    id_da_tag: `${channel}.marketplace.${id_da_tag}`,
    webview: "safra-marketplace",
    ...additionalParams
  };

  dataLayer.push(buttonEvent);
};

/**
* Hotel payment event (Payment Page).
* Required params:
* hotel_name, room_name, points, cash, total_value, total_points,
* check_in, check_out, payment, nights, stars, hosts
*/
export const trackHotelPayment = (params = {}) =>
  sendGA4Event("payment", {
    hotel_name: params.hotel_name,
    room_name: params.room_name,
    points: params.points,
    cash: params.cash,
    total_value: params.total_value,
    total_points: params.total_points,
    check_in: params.check_in,
    check_out: params.check_out,
    payment: params.payment,
    nights: params.nights,
    stars: params.stars,
    hosts: params.hosts,
  });

/**
* Hotel booking event (Confirmation Page).
* Required params:
* order_id, hotel_name, room_name, points, cash, total_value, total_points,
* check_in, check_out, nights, stars, hosts
*/
export const trackHotelBooking = (params = {}) =>
  sendGA4Event("booking", {
    order_id: params.order_id,
    hotel_name: params.hotel_name,
    room_name: params.room_name,
    points: params.points,
    cash: params.cash,
    total_value: params.total_value,
    total_points: params.total_points,
    check_in: params.check_in,
    check_out: params.check_out,
    nights: params.nights,
    stars: params.stars,
    hosts: params.hosts,
  });

  export const trackHotelCallback = (params) => {
  sendGA4Event("callback", {
    status: params.status,
    action: params.action,
    error: params.error || "",
    event_category: "hotel_journey",
    event_label: `callback_${params.action}_${params.status}`,
  });
};


/**
 * Reusable function for sending product events to GA4/GTM dataLayer.
 * Reusable for all e-commerce funnel events.
 *
 * @param {string} eventType - Event type (view_item, add_to_cart, purchase, etc.)
 * @param {object} params - Product parameters
 * @param {string} params.currency - Currency (default: "BRL")
 * @param {number} params.value - Total product value
 * @param {string} params.item_id - Product ID
 * @param {string} params.item_name - Product name
 * @param {string} params.item_brand - Product brand
 * @param {string} params.item_category - Product category
 * @param {number} params.price - Product price
 * @param {number} params.quantity - Quantity (default: 1)
 * @param {string} params.id_da_tag - Tag identifier
 * @param {string} params.webview - Webview identifier (default: "safra-marketplace")
 * @param {...*} params.additionalParams - Any additional parameters can be passed and will be included in the event
 */
export const sendProductEvent = (eventType, params = {}) => {
  if (!eventType) {
    console.warn("sendProductEvent: eventType is required");
    return;
  }

  const dataLayer = getDataLayer();
  if (!dataLayer) return;

  const {
    currency,
    value,
    item_id,
    item_name,
    item_brand,
    item_category,
    price,
    quantity,
    id_da_tag,
    webview,
    ...additionalParams
  } = params;

  const productEvent = {
    event: eventType,
    currency: currency || "BRL",
    value: value,
    item_id: item_id,
    item_name: item_name,
    item_brand: item_brand,
    item_category: item_category,
    price: price,
    quantity: quantity || 1,
    id_da_tag: id_da_tag,
    webview: webview || "safra-marketplace",
    ...additionalParams,
  };

  dataLayer.push(productEvent);
};

/**
 * Reusable function for sending banner events to GA4/GTM dataLayer.
 * Specifically designed for banner impression tracking.
 *
 * @param {object} params - Banner parameters
 * @param {number} params.index - Banner position in carousel
 * @param {string} params.custom_title - Banner name/title
 * @param {string} params.content_type - Category or brand
 * @param {string} params.Nome_Da_Tela - Screen name (default: "marketplace-home")
 * @param {string} params.data_inicio - Banner start date
 * @param {string} params.data_termino - Banner end date
 * @param {string} params.url_imagem - Banner image URL
 * @param {...*} params.additionalParams - Any additional parameters can be passed and will be included in the event
 */
export const sendBannerEvent = (eventType, params = {}) => {
  if (!eventType) {
    console.warn("sendBannerEvent: eventType is required");
    return;
  }

  const dataLayer = getDataLayer();
  if (!dataLayer) return;

  const {
    index,
    custom_title,
    content_type,
    Nome_Da_Tela,
    data_inicio,
    data_termino,
    url_imagem,
    id_da_tag,
    ...additionalParams
  } = params;

  const bannerEvent = {
    event: eventType,
    elemento: "banner-carrossel",
    index: index,
    custom_title: custom_title,
    content_type: content_type,
    Nome_Da_Tela: Nome_Da_Tela,
    data_inicio: data_inicio,
    data_termino: data_termino,
    url_imagem: url_imagem,
    funcionalidades: "marketplace",
    id_da_tag: id_da_tag,
    webview: "safra-marketplace",
    ...additionalParams,
  };

  dataLayer.push(bannerEvent);
};

/**
 * Sends a view_item_list event to GA4/GTM dataLayer.
 *
 * @param {string} itemListName - Name of the product list (e.g. "mais-vendidos", "bebidas")
 * @param {Array} products - Array of product objects
 * @param {object} [options] - Optional overrides
 * @param {Function} [options.mapProduct] - Custom mapper for product → item fields
 */
export const sendViewItemListEvent = (itemListName, products, options = {}) => {
  if (!itemListName || !products || products.length === 0) return;

  const dataLayer = getDataLayer();
  if (!dataLayer) return;

  const mapProduct =
    options.mapProduct ||
    ((product, index) => ({
      item_id: product.uuid || product.id || "",
      item_name: product.label || "",
      affiliation: product.seller_name || "",
      discount: product.product_price
        ? Math.max(
            0,
            (product.product_price.price_suggested || 0) -
              (product.product_price.price || 0),
          )
        : 0,
      index: index,
      item_brand: product.brand_name || "",
      item_category: product.product_categories?.[0]?.category_name || "",
      price: product.product_price?.price || 0,
      quantity: 1,
    }));

  const items = products.map((product, index) => mapProduct(product, index));

  dataLayer.push({
    event: "view_item_list",
    currency: "BRL",
    item_list_name: itemListName,
    items: items,
    id_da_tag: "safra.marketplace.ecommerce.3",
    webview: "safra-marketplace",
  });

  if (process.env.NODE_ENV === "development") {
    console.log("GA4 Event: view_item_list", {
      item_list_name: itemListName,
      items,
    });
  }
};

/**
 * Sends a select_item event to GA4/GTM dataLayer.
 *
 * @param {string} itemListName - Name of the product list where the item was selected
 * @param {object} product - Product data
 * @param {number} index - Product index in the list
 */
export const sendSelectItemEvent = (itemListName, product, index) => {
  if (!product) return;

  const dataLayer = getDataLayer();
  if (!dataLayer) return;

  const item = mapListItemToGA4(product, index);

  dataLayer.push({
    event: "select_item",
    currency: "BRL",
    item_list_name: itemListName,
    items: [item],
    id_da_tag: "safra.marketplace.ecommerce.5",
    webview: "safra-marketplace",
  });

  if (process.env.NODE_ENV === "development") {
    console.log("GA4 Event: select_item", {
      item_list_name: itemListName,
      item,
    });
  }
};

/**
 * Sends an add_to_wishlist event to GA4/GTM dataLayer.
 *
 * @param {string} itemListName - Name of the product list where the item was favorited
 * @param {object} product - Product data
 * @param {number} index - Product index in the list
 */
export const sendAddToWishlistEvent = (itemListName, product, index) => {
  if (!product) return;

  const dataLayer = getDataLayer();
  if (!dataLayer) return;

  const item = mapListItemToGA4(product, index);

  dataLayer.push({
    event: "add_to_wishlist",
    currency: "BRL",
    item_list_name: itemListName,
    items: [item],
    id_da_tag: "safra.marketplace.ecommerce.4",
    webview: "safra-marketplace",
  });

  if (process.env.NODE_ENV === "development") {
    console.log("GA4 Event: add_to_wishlist", {
      item_list_name: itemListName,
      item,
    });
  }
};

/**
 * Sends a view_item event to GA4/GTM dataLayer.
 *
 * @param {object} product - Product data from PDP API
 */
export const sendViewItemEvent = (product) => {
  if (!product) return;

  const dataLayer = getDataLayer();
  if (!dataLayer) return;

  const item = mapPdpItemToGA4(product);

  dataLayer.push({
    event: "view_item",
    currency: "BRL",
    value: item.price,
    items: [item],
    id_da_tag: "safra.marketplace.ecommerce.6",
    webview: "safra-marketplace",
  });

  if (process.env.NODE_ENV === "development") {
    console.log("GA4 Event: view_item", { value: item.price, item });
  }
};

/**
 * Sends an add_to_cart event to GA4/GTM dataLayer.
 *
 * @param {object} product - Product data from PDP API
 */
export const sendAddToCartEvent = (product) => {
  if (!product) return;

  const dataLayer = getDataLayer();
  if (!dataLayer) return;

  const item = mapPdpItemToGA4(product);

  dataLayer.push({
    event: "add_to_cart",
    currency: "BRL",
    value: item.price,
    items: [item],
    id_da_tag: "safra.marketplace.ecommerce.7",
    webview: "safra-marketplace",
  });

  if (process.env.NODE_ENV === "development") {
    console.log("GA4 Event: add_to_cart", { value: item.price, item });
  }
};

/**
 * Sends a view_cart event to GA4/GTM dataLayer.
 *
 * @param {Array} cartItems - Array of cart item objects
 */
export const sendViewCartEvent = (cartItems) => {
  if (!cartItems || cartItems.length === 0) return;

  const dataLayer = getDataLayer();
  if (!dataLayer) return;

  const items = cartItems.map(mapCartItemToGA4);

  const totalValue = calcTotalValue(items);

  dataLayer.push({
    event: "view_cart",
    currency: "BRL",
    value: totalValue,
    items: items,
    id_da_tag: "safra.marketplace.ecommerce.8",
    webview: "safra-marketplace",
  });

  if (process.env.NODE_ENV === "development") {
    console.log("GA4 Event: view_cart", { value: totalValue, items });
  }
};

/**
 * Sends a remove_from_cart event to GA4/GTM dataLayer.
 *
 * @param {object} product - Cart item product data
 * @param {number} index - Product index in the cart
 */
export const sendRemoveFromCartEvent = (product, index = 0) => {
  if (!product) return;

  const dataLayer = getDataLayer();
  if (!dataLayer) return;

  const item = mapCartItemToGA4(product, index);

  dataLayer.push({
    event: "remove_from_cart",
    currency: "BRL",
    value: item.price,
    items: [item],
    id_da_tag: "safra.marketplace.ecommerce.9",
    webview: "safra-marketplace",
  });

  if (process.env.NODE_ENV === "development") {
    console.log("GA4 Event: remove_from_cart", { value: item.price, item });
  }
};

/**
 * Sends a begin_checkout event to GA4/GTM dataLayer.
 *
 * @param {Array} cartItems - Array of cart item objects
 */
export const sendBeginCheckoutEvent = (cartItems) => {
  if (!cartItems || cartItems.length === 0) return;

  const dataLayer = getDataLayer();
  if (!dataLayer) return;

  const items = cartItems.map(mapCartItemToGA4);

  const totalValue = calcTotalValue(items);

  dataLayer.push({
    event: "begin_checkout",
    currency: "BRL",
    value: totalValue,
    items: items,
    id_da_tag: "safra.marketplace.ecommerce.10",
    webview: "safra-marketplace",
  });

  if (process.env.NODE_ENV === "development") {
    console.log("GA4 Event: begin_checkout", { value: totalValue, items });
  }
};

/**
 * Sends an add_shipping_info event to GA4/GTM dataLayer.
 *
 * @param {Array} cartItems - Array of cart item objects
 * @param {object} shippingByBrand - Shipping data keyed by brand_uuid
 */
export const sendAddShippingInfoEvent = (cartItems, shippingByBrand = {}) => {
  if (!cartItems || cartItems.length === 0) return;

  const dataLayer = getDataLayer();
  if (!dataLayer) return;

  const items = cartItems.map((product, index) => {
    const item = mapCartItemToGA4(product, index);
    const shipping = shippingByBrand[product.brand_uuid] || {};
    const deadline = shipping.shipping_deadline || 0;
    return { ...item, shipping_tier: deadline ? `frete-${deadline}-dias-uteis` : "" };
  });

  const totalValue = calcTotalValue(items);

  dataLayer.push({
    event: "add_shipping_info",
    currency: "BRL",
    value: totalValue,
    items: items,
    id_da_tag: "safra.marketplace.ecommerce.11",
    webview: "safra-marketplace",
  });

  if (process.env.NODE_ENV === "development") {
    console.log("GA4 Event: add_shipping_info", { value: totalValue, items });
  }
};

/**
 * Sends an add_payment_info event to GA4/GTM dataLayer.
 *
 * @param {Array} cartItems - Array of cart item objects
 * @param {string} paymentType - Payment type (e.g. "credit_card")
 */
export const sendAddPaymentInfoEvent = (cartItems, paymentType) => {
  if (!cartItems || cartItems.length === 0) return;

  const dataLayer = getDataLayer();
  if (!dataLayer) return;

  const items = cartItems.map(mapCartItemToGA4);

  const totalValue = calcTotalValue(items);

  dataLayer.push({
    event: "add_payment_info",
    currency: "BRL",
    value: totalValue,
    payment_type: paymentType || "",
    items: items,
    id_da_tag: "safra.marketplace.ecommerce.12",
    webview: "safra-marketplace",
  });

  if (process.env.NODE_ENV === "development") {
    console.log("GA4 Event: add_payment_info", {
      value: totalValue,
      payment_type: paymentType,
      items,
    });
  }
};

/**
 * Sends a purchase event to GA4/GTM dataLayer.
 *
 * @param {object} params - Purchase parameters
 * @param {string} params.transactionId - Order/transaction ID
 * @param {Array} params.cartItems - Array of cart item objects
 * @param {number} params.shipping - Shipping total value
 * @param {string} params.coupon - Coupon code (if any)
 * @param {number} params.value - Total purchase value
 */
export const sendPurchaseEvent = ({
  transactionId,
  cartItems,
  shipping = 0,
  coupon = "",
  value = 0,
}) => {
  if (!cartItems || cartItems.length === 0) return;

  const dataLayer = getDataLayer();
  if (!dataLayer) return;

  const items = cartItems.map(mapCartItemToGA4);

  dataLayer.push({
    event: "purchase",
    currency: "BRL",
    value: value,
    transaction_id: transactionId || "",
    shipping: shipping,
    coupon: coupon,
    items: items,
    id_da_tag: "safra.marketplace.ecommerce.13",
    webview: "safra-marketplace",
  });

  if (process.env.NODE_ENV === "development") {
    console.log("GA4 Event: purchase", {
      transaction_id: transactionId,
      value,
      shipping,
      coupon,
      items,
    });
  }
};

/**
 * Flight payment event — fired when user clicks "RESGATAR" on airfare checkout.
 *
 * @param {object} params
 * @param {string} params.departure - Departure airport/city (e.g. "São Paulo (CGH)")
 * @param {string} params.arrival - Arrival airport/city (e.g. "Rio de Janeiro (SDU)")
 * @param {string} params.depating - Departure date (e.g. "5/20/2025")
 * @param {string} params.returning - Return date (e.g. "5/30/2025")
 * @param {number} params.passengers - Total number of passengers
 * @param {string} params.class - Cabin class (e.g. "Econômica", "Executiva")
 * @param {string} params.type - Trip type (e.g. "Retorno", "Somente ida")
 * @param {number} params.adults - Number of adult passengers
 * @param {string} params.payment - Payment method (e.g. "Pontos + Cartão", "Pontos")
 * @param {number} params.children - Number of children passengers
 * @param {number} params.baby - Number of infant passengers
 * @param {number} params.total_cash - Total monetary value of the flight (BRL)
 * @param {number} params.cash - Monetary amount being paid (BRL)
 * @param {number} params.total_points - Total points value of the flight
 * @param {number} params.points - Points amount being used
 */
export const trackFlightPayment = (params = {}) =>
  sendGA4Event("flight_payment", {
    departure: params.departure || "",
    arrival: params.arrival || "",
    depating: params.depating || "",
    returning: params.returning || "",
    passengers: params.passengers || 0,
    class: params.class || "",
    type: params.type || "",
    adults: params.adults || 0,
    payment: params.payment || "",
    children: params.children || 0,
    baby: params.baby || 0,
    total_cash: params.total_cash || 0,
    cash: params.cash || 0,
    total_points: params.total_points || 0,
    points: params.points || 0,
  });

/**
 * Flight transaction event — fired on successful airfare purchase confirmation.
 *
 * @param {object} params
 * @param {string} params.departure - Departure airport/city (e.g. "São Paulo (CGH)")
 * @param {string} params.arrival - Arrival airport/city (e.g. "Rio de Janeiro (SDU)")
 * @param {string} params.depating - Departure date (e.g. "5/20/2025")
 * @param {string} params.returning - Return date (e.g. "5/30/2025")
 * @param {number} params.passengers - Total number of passengers
 * @param {string} params.class - Cabin class (e.g. "Econômica", "Executiva")
 * @param {string} params.type - Trip type (e.g. "Retorno", "Somente ida")
 * @param {number} params.adults - Number of adult passengers
 * @param {number} params.children - Number of children passengers
 * @param {number} params.baby - Number of infant passengers
 * @param {string} params.order_id - Order UUID
 * @param {string} params.payment - Payment method (e.g. "Pontos + Cartão", "Pontos")
 * @param {number} params.total_cash - Total monetary value of the flight (BRL)
 * @param {number} params.cash - Monetary amount paid (BRL)
 * @param {number} params.total_points - Total points value of the flight
 * @param {number} params.points - Points amount used
 */
export const trackFlightTransaction = (params = {}) =>
  sendGA4Event("flight_transaction", {
    departure: params.departure || "",
    arrival: params.arrival || "",
    depating: params.depating || "",
    returning: params.returning || "",
    passengers: params.passengers || 0,
    class: params.class || "",
    type: params.type || "",
    adults: params.adults || 0,
    children: params.children || 0,
    baby: params.baby || 0,
    order_id: params.order_id || "",
    payment: params.payment || "",
    total_cash: params.total_cash || 0,
    cash: params.cash || 0,
    total_points: params.total_points || 0,
    points: params.points || 0,
  });
